import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Select as ChakraSelect,
  useToast,
  Text,
  Icon,
  Badge,
  Box,
  Avatar,
  Switch,
} from '@chakra-ui/react';
import { FiBriefcase } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { PRODUCT_TYPES, LEAD_SOURCES } from '../../constants/crmConstants';
import ContactTypeBadges from './ContactTypeBadges';
import { supabase } from '../../utils/supabase';

/**
 * Quick Add Deal Modal
 * 
 * Fast deal creation from a contact.
 * Pre-fills contact info, focuses on deal details.
 * 
 * @param {object} contact - Pre-filled contact object
 * @param {function} onDealCreated - Callback with created deal
 * @param {boolean} openAfterCreate - Open deal drawer after creation
 */
export default function QuickAddDeal({ 
  isOpen, 
  onClose, 
  contact,
  onDealCreated,
  openAfterCreate = false,
}) {
  const { currentUser, SuperAdmin, Roles } = useAuth();
  const toast = useToast();
  const history = useHistory();
  const isCRMAdmin = SuperAdmin || Roles?.includes('CRM Admin');

  const [product, setProduct] = useState('');
  const [source, setSource] = useState('manual');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Admin-only: create as a company record. Defaults to the contact's scope so
  // deals for company contacts stay company-visible unless the admin opts out.
  const [isCompanyRecord, setIsCompanyRecord] = useState(false);
  useEffect(() => {
    if (isOpen) setIsCompanyRecord(contact?.recordScope === 'company');
  }, [isOpen, contact?.recordScope]);

  const handleSubmit = async (shouldOpen = false) => {
    if (!contact) {
      toast({
        title: 'No contact selected',
        status: 'error',
        duration: 2000,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Create deal in Supabase - only include columns that exist in schema.
      // arive_id must be NULL (not '') so nothing mistakes this for an
      // Arive-linked deal; amount stays NULL when the user leaves it blank.
      const parsedAmount = amount.trim() ? parseFloat(amount) : NaN;
      const dealData = {
        contact_id: contact.id,
        deal_name: `${contact.firstName} ${contact.lastName}`,
        stage: 'lead-new',
        product: product || null,
        amount: Number.isFinite(parsedAmount) ? parsedAmount : null,
        estimated_closing_date: null,
        next_action: 'follow-up-call',
        next_action_due: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        is_at_risk: false,
        arive_id: null,
        source_medium: source || 'manual',
        source_campaign: null,
        owner_id: contact.ownerId || currentUser.uid,
        // Admin-only company records; everyone else keeps the column default
        ...(isCRMAdmin && isCompanyRecord ? { record_scope: 'company' } : {}),
      };

      const { data: newDeal, error: dealError } = await supabase
        .from('crm_deals')
        .insert(dealData)
        .select()
        .single();

      if (dealError) throw dealError;

      // Add initial note if provided
      if (notes.trim()) {
        const { error: noteError } = await supabase
          .from('crm_activities')
          .insert({
            deal_id: newDeal.id,
            type: 'note',
            content: notes.trim(),
            author_id: currentUser.uid,
            metadata: {
              activityType: 'created',
              disposition: '',
              authorName: currentUser.displayName || currentUser.email,
              authorPhotoURL: currentUser.photoURL || '',
            },
          });

        if (noteError) {
          console.error('Error adding initial note:', noteError);
        }
      }

      toast({
        title: 'Deal created',
        description: `New deal for ${contact.firstName} ${contact.lastName}`,
        status: 'success',
        duration: 3000,
      });

      // Reset form
      setProduct('');
      setSource('manual');
      setAmount('');
      setNotes('');

      if (onDealCreated) {
        // Transform to camelCase for frontend compatibility
        onDealCreated({
          id: newDeal.id,
          contactId: newDeal.contact_id,
          dealName: newDeal.deal_name,
          stage: newDeal.stage,
          source: newDeal.source_campaign,
          product: newDeal.product,
          amount: newDeal.amount,
          ownerId: newDeal.owner_id,
          contact,
        });
      }

      onClose();

      if (shouldOpen || openAfterCreate) {
        history.push(`/crm/deals/${newDeal.id}`);
      }
    } catch (error) {
      console.error('Error creating deal:', error);
      toast({
        title: 'Error creating deal',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!contact) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2}>
            <Icon as={FiBriefcase} />
            <Text>Create Deal</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Contact Info Display */}
            <Box p={4} bg="purple.50" borderRadius="md" borderWidth="1px" borderColor="purple.200">
              <HStack spacing={3}>
                <Avatar
                  size="sm"
                  name={`${contact.firstName} ${contact.lastName}`}
                />
                <VStack align="flex-start" spacing={0} flex={1}>
                  <Text fontWeight="semibold" fontSize="sm">
                    {contact.firstName} {contact.lastName}
                  </Text>
                  <ContactTypeBadges
                    contactTypes={contact.contactTypes || []}
                    primaryContactType={contact.primaryContactType}
                    size="xs"
                    maxDisplay={2}
                  />
                  <HStack spacing={2} mt={1}>
                    {contact.email && (
                      <Text fontSize="xs" color="gray.600">{contact.email}</Text>
                    )}
                    {contact.phone && (
                      <Text fontSize="xs" color="gray.600">{contact.phone}</Text>
                    )}
                  </HStack>
                </VStack>
              </HStack>
            </Box>

            {/* Deal Details */}
            <FormControl>
              <FormLabel fontSize="sm">Product</FormLabel>
              <ChakraSelect
                value={product}
                onChange={(e) => setProduct(e.target.value)}
                placeholder="Select product type..."
              >
                {PRODUCT_TYPES.map(p => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </ChakraSelect>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Source</FormLabel>
              <ChakraSelect
                value={source}
                onChange={(e) => setSource(e.target.value)}
              >
                {LEAD_SOURCES.map(s => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </ChakraSelect>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Loan Amount</FormLabel>
              <Input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="250000"
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Initial Notes (Optional)</FormLabel>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any important details..."
              />
            </FormControl>

            {/* Admin-only: company record scope */}
            {isCRMAdmin && (
              <FormControl display="flex" alignItems="center">
                <Switch
                  size="sm"
                  colorScheme="purple"
                  isChecked={isCompanyRecord}
                  onChange={(e) => setIsCompanyRecord(e.target.checked)}
                  mr={2}
                />
                <FormLabel fontSize="sm" mb={0} mr={2}>
                  Company record
                </FormLabel>
                <Badge colorScheme="purple" fontSize="xs">visible to all CRM admins</Badge>
              </FormControl>
            )}

            <Text fontSize="xs" color="gray.600">
              Deal will be created with default next action: "Follow up call" (due tomorrow)
            </Text>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            variant="outline"
            mr={2}
            onClick={() => handleSubmit(false)}
            isLoading={isSubmitting}
            loadingText="Creating..."
          >
            Create Deal
          </Button>
          <Button
            colorScheme="blue"
            onClick={() => handleSubmit(true)}
            isLoading={isSubmitting}
            loadingText="Creating..."
          >
            Create & Open
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

